import {
  Accordion,
  Alert,
  Box,
  ContentBlock,
  Heading,
  Stack,
  Strong,
  Text,
} from 'braid-design-system';

import { Nav } from 'src/components/Nav/Nav';
import { PageWrapper } from 'src/components/PageWrapper/PageWrapper';
import type { PageProps } from 'src/types';
import { slugify } from 'src/utils/slugify';

import { ApiVersioning } from './components/ApiVersioning';
import { Branding } from './components/Branding';
import { ComplianceWithLawAndTerms } from './components/ComplianceWithLawAndTerms';
import { ConfidentialInformation } from './components/ConfidentialInformation';
import { DataSecurity } from './components/DataSecurity';
import { Definitions } from './components/Definitions';
import { Implementation } from './components/Implementation';
import { Indemnity } from './components/Indemnity';
import { Integration } from './components/Integration';
import { IntellectualProperty } from './components/IntellectualProperty';
import { LimitationOfLiability } from './components/LimitationOfLiability';
import { Miscellaneous } from './components/Miscellaneous';
import { Privacy } from './components/Privacy';
import { RightsReservedByUs } from './components/RightsReservedByUs';
import { SectionA } from './components/SectionA';
import { SectionB } from './components/SectionB';
import { SectionC } from './components/SectionC';
import { SectionD } from './components/SectionD';
import { SectionE } from './components/SectionE';
import { SectionF } from './components/SectionF';
import { SectionG } from './components/SectionG';
import { SectionH } from './components/SectionH';
import { SectionI } from './components/SectionI';
import { SeekApiProhibitions } from './components/SeekApiProhibitions';
import { Support } from './components/Support';
import { TermAndTermination } from './components/TermAndTermination';
import { VariationOfTerms } from './components/VariationOfTerms';
import { YourObligations } from './components/YourObligations';

export const TermsOfUse = ({ slug }: PageProps) => {
  const pageTrackingProps = {
    page: `${slugify({ str: slug })}`,
    eventName: `${slugify({ str: slug })}-viewed`,
    siteSection: 'partners',
  };

  return (
    <PageWrapper pageTrackingProps={pageTrackingProps} title={slug}>
      <Nav activeTab="terms-of-use" />
      <Box
        paddingY="xlarge"
        paddingBottom="xxlarge"
        paddingX={['gutter', 'none']}
      >
        <ContentBlock>
          <Stack space="xlarge">
            <Box>
              <Heading level="1">SEEK API Terms of Use</Heading>
            </Box>
            <Stack space="large">
              <Stack space="gutter">
                <Alert tone="info">
                  <Stack space="medium">
                    <Text>
                      <em>Last Updated 23 April 2024.</em>
                    </Text>
                  </Stack>
                </Alert>
                <Text>
                  These SEEK API Terms of Use (<Strong>Terms</Strong>) govern
                  the use of the SEEK API (as defined below). By accessing and
                  using the SEEK API, you are agreeing to enter into a legally
                  binding contract in consideration of the mutual promises
                  contained in these Terms.
                </Text>
              </Stack>
              <Accordion>
                <Definitions />
                <VariationOfTerms />
                <Integration />
                <Implementation />
                <IntellectualProperty />
                <Branding />
                <SeekApiProhibitions />
                <ConfidentialInformation />
                <ComplianceWithLawAndTerms />
                <DataSecurity />
                <YourObligations />
                <Privacy />
                <ApiVersioning />
                <Support />
                <RightsReservedByUs />
                <Indemnity />
                <LimitationOfLiability />
                <TermAndTermination />
                <Miscellaneous />
              </Accordion>
              <Box paddingTop="gutter">
                <Text weight="strong">
                  <em>
                    The sections below may be applicable to you depending on the
                    types of functionality you have chosen through the SEEK API.
                    These sections form part of the Terms:
                  </em>
                </Text>
              </Box>
              <Accordion>
                <SectionA />
                <SectionB />
                <SectionC />
                <SectionD />
                <SectionE />
                <SectionF />
                <SectionG />
                <SectionH />
                <SectionI />
              </Accordion>
            </Stack>
          </Stack>
        </ContentBlock>
      </Box>
    </PageWrapper>
  );
};
